import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  AlertTriangle, 
  Calendar, 
  Shield, 
  Upload, 
  Trash2, 
  Eye, 
  FileText, 
  X,
  Settings,
  Heart,
  UserCheck
} from 'lucide-react';
import { SensitiveDataBanner } from '../components/SensitiveDataBanner';
import {
  formatDateForInput,
  formatPhoneAsYouType,
  isMinor as isMinorFromBirthDate,
  parseInternationalPhone,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { HelpIcon } from '../components/HelpIcon';
import { useToast } from '../components/Toast';
import { apiClient } from '../utils/api';
import { InternationalPhoneInput, type InternationalPhoneChangeResult } from '../components/InternationalPhoneInput';
import { DateInput } from '../components/DateInput';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string | null;
  isMinor: boolean | null;
  isAnonymous?: boolean;
  anonymousCode?: string | null;
  legalGuardianId?: string;
  identityDocument?: {
    type: string;
    number: string;
    issueDate: string;
    expiryDate?: string;
    issuePlace: string;
    issuingAuthority: string;
    fileUrl?: string;
    fileName?: string;
  };
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email: string;
  };
  medicalInfo?: {
    allergies: string[];
    medications: string[];
    conditions: string[];
    bloodType?: string;
    pregnancyStatus?: string;
    lastTetanusShot?: string;
  };
  preferences: {
    communicationMethod: string;
    reminderSettings: {
      enabled: boolean;
      daysBefore: number[];
      methods: string[];
    };
    privacySettings: {
      allowPhotography: boolean;
      allowGalleryUse: boolean;
      allowTestimonials: boolean;
      allowMarketing: boolean;
    };
  };
  /** Real SMS gates used by notificationService (not preferences JSON) */
  smsConsent?: boolean;
  phoneVerified?: boolean;
  reminderChannels?: string[];
  status: string;
  legalGuardian?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    relationshipToMinor: string;
    relationshipDetails?: string;
    identityDocument: {
      type: string;
      number: string;
      issueDate: string;
      expiryDate?: string;
      issuePlace: string;
      issuingAuthority: string;
      fileUrl?: string;
      fileName?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

function getClientEditTitleName(client: Pick<Client, 'firstName' | 'lastName' | 'isAnonymous' | 'anonymousCode'>): string {
  const firstName = (client.firstName || '').trim();
  const lastName = (client.lastName || '').trim();
  const code = (client.anonymousCode || '').trim();
  const isAnonymous = Boolean(client.isAnonymous) || Boolean(code);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  if (isAnonymous) {
    const label = fullName || firstName || 'Client anonyme';
    return code ? `${label} (${code})` : label;
  }

  return fullName || 'Client';
}

function formatCommaSeparatedList(items?: string[] | string | null): string {
  if (Array.isArray(items)) {
    return items.join(', ');
  }
  if (typeof items === 'string') {
    return items;
  }
  return '';
}

function normalizeMedicalList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter((item) => item.trim().length > 0);
  }
  if (typeof value === 'string') {
    return parseCommaSeparatedList(value);
  }
  return [];
}

function parseCommaSeparatedList(text: string): string[] {
  return text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

const getPhoneState = (
  value: string,
  fallbackCountry: SupportedPhoneCountry = 'FR',
): InternationalPhoneChangeResult => {
  const parsed = parseInternationalPhone(value, fallbackCountry);
  const country = parsed.country ?? fallbackCountry;
  return {
    displayValue: formatPhoneAsYouType(value, country),
    e164Value: parsed.isValid ? parsed.e164 : null,
    country,
    isValid: parsed.isValid,
  };
};

export function ClientEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast, ToastComponent } = useToast();
  
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [phoneState, setPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [emergencyPhoneState, setEmergencyPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [guardianPhoneState, setGuardianPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingGuardianDocument, setUploadingGuardianDocument] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [medicalListText, setMedicalListText] = useState({
    allergies: '',
    medications: '',
    conditions: '',
  });

  const syncMedicalListTextFromClient = (loadedClient: Client) => {
    setMedicalListText({
      allergies: formatCommaSeparatedList(normalizeMedicalList(loadedClient.medicalInfo?.allergies)),
      medications: formatCommaSeparatedList(normalizeMedicalList(loadedClient.medicalInfo?.medications)),
      conditions: formatCommaSeparatedList(normalizeMedicalList(loadedClient.medicalInfo?.conditions)),
    });
  };

  const applyMedicalListTextToClient = (base: Client): Client => {
    if (!base.medicalInfo) return base;

    return {
      ...base,
      medicalInfo: {
        ...base.medicalInfo,
        allergies: parseCommaSeparatedList(medicalListText.allergies),
        medications: parseCommaSeparatedList(medicalListText.medications),
        conditions: parseCommaSeparatedList(medicalListText.conditions),
      },
    };
  };

  useEffect(() => {
    if (id) {
      fetchClient(id);
    }
  }, [id]);

  const fetchClient = async (clientId: string) => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      
      const response = await apiClient(`/api/admin/clients/${clientId}`);
      const data = await response.json();

      if (data.success) {
        const loadedClient: Client = data.data.client;
        const nextPhoneState = getPhoneState(loadedClient.phone || '');
        const nextEmergencyPhoneState = getPhoneState(loadedClient.emergencyContact?.phone || '');
        const nextGuardianPhoneState = getPhoneState(loadedClient.legalGuardian?.phone || '');
        const formattedClient: Client = {
          ...loadedClient,
          isAnonymous: Boolean(loadedClient.isAnonymous) || Boolean(loadedClient.anonymousCode),
          anonymousCode: loadedClient.anonymousCode || null,
          phone: nextPhoneState.displayValue,
          emergencyContact: {
            ...loadedClient.emergencyContact,
            phone: nextEmergencyPhoneState.displayValue,
          },
          legalGuardian: loadedClient.legalGuardian
            ? { ...loadedClient.legalGuardian, phone: nextGuardianPhoneState.displayValue }
            : undefined,
        };
        setPhoneState(nextPhoneState);
        setEmergencyPhoneState(nextEmergencyPhoneState);
        setGuardianPhoneState(nextGuardianPhoneState);
        setClient(formattedClient);
        syncMedicalListTextFromClient(formattedClient);
      } else {
        setError(data.message || 'Échec du chargement du client');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Fetch client error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!client) return;
    
    setClient(prev => {
      if (!prev) return prev;
      
      const keys = field.split('.');
      const newClient = { ...prev };
      let current: any = newClient;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      if (field === 'dateOfBirth') {
        newClient.isMinor = value
          ? isMinorFromBirthDate(String(value))
          : null;
      } else if (field === 'isMinor') {
        newClient.isMinor = value === true ? true : null;
      }
      return newClient;
    });
    
    setHasUnsavedChanges(true);
  };

  const handleAddLegalGuardian = () => {
    if (!client?.isMinor) return;
    const emptyPhoneState = getPhoneState('');
    setGuardianPhoneState(emptyPhoneState);
    handleInputChange('legalGuardian', {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      relationshipToMinor: 'parent',
      relationshipDetails: '',
      identityDocument: {
        type: 'national_id',
        number: '',
        issueDate: '',
        expiryDate: '',
        issuePlace: '',
        issuingAuthority: '',
        fileUrl: '',
        fileName: '',
      },
    });
  };

  const handleDocumentUpload = async (file: File, isGuardianDocument = false) => {
    try {
      if (isGuardianDocument) {
        setUploadingGuardianDocument(true);
      } else {
        setUploadingDocument(true);
      }

      // Validate file
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Type de fichier non supporté. Utilisez JPG, PNG ou PDF');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB
        throw new Error('Le fichier est trop volumineux. Maximum 5MB');
      }

      // Upload as multipart so the file bytes are actually persisted
      const formData = new FormData();
      formData.append('clientId', client!.id);
      formData.append('documentType', isGuardianDocument ? 'guardian_identity' : 'client_identity');
      formData.append('file', file);

      const response = await apiClient('/api/admin/clients/upload-document', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Échec du téléchargement du document');
      }

      const data = await response.json();
      if (data.success) {
        // Update the document with file information
        if (isGuardianDocument && client?.legalGuardian) {
          handleInputChange('legalGuardian.identityDocument.fileUrl', data.data.fileUrl);
          handleInputChange('legalGuardian.identityDocument.fileName', file.name);
        } else {
          // Initialize identityDocument if it doesn't exist
          if (!client?.identityDocument) {
            handleInputChange('identityDocument', {
              type: 'national_id',
              number: '',
              issueDate: '',
              expiryDate: '',
              issuePlace: '',
              issuingAuthority: '',
              fileUrl: '',
              fileName: ''
            });
          }
          handleInputChange('identityDocument.fileUrl', data.data.fileUrl);
          handleInputChange('identityDocument.fileName', file.name);
        }
      } else {
        throw new Error(data.message || 'Échec du téléchargement');
      }
    } catch (error) {
      console.error('Document upload error:', error);
      setError(error instanceof Error ? error.message : 'Échec du téléchargement du document');
    } finally {
      if (isGuardianDocument) {
        setUploadingGuardianDocument(false);
      } else {
        setUploadingDocument(false);
      }
    }
  };

  const handleDocumentDelete = (isGuardianDocument = false) => {
    if (isGuardianDocument && client?.legalGuardian) {
      handleInputChange('legalGuardian.identityDocument.fileUrl', '');
      handleInputChange('legalGuardian.identityDocument.fileName', '');
    } else {
      handleInputChange('identityDocument.fileUrl', '');
      handleInputChange('identityDocument.fileName', '');
    }
  };

  const handleDocumentView = async (fileUrl: string) => {
    try {
      const response = await apiClient(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch document');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      console.error('Document view error:', error);
      setError('Impossible d\'afficher le document');
    }
  };

  const renderDocumentSection = (document: any, isGuardianDocument = false) => {
    const hasFile = document?.fileUrl && document?.fileName;
    const uploading = isGuardianDocument ? uploadingGuardianDocument : uploadingDocument;

    return (
      <div className="space-y-4">
        {/* Document File Management */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {hasFile ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">{document.fileName}</p>
                  <p className="text-sm text-gray-500">Document téléchargé</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => handleDocumentView(document.fileUrl)}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Voir le document"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDocumentDelete(isGuardianDocument)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer le document"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {isGuardianDocument ? 'Aucun document du représentant' : 'Aucun document d\'identité'}
                </p>
                <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Téléchargement...' : 'Télécharger un document'}
                  <input
                    type="file"
                    className="sr-only"
                    accept=".jpg,.jpeg,.png,.pdf"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleDocumentUpload(file, isGuardianDocument);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Replace Document Button */}
        {hasFile && (
          <div className="flex justify-center">
            <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Téléchargement...' : 'Remplacer le document'}
              <input
                type="file"
                className="sr-only"
                accept=".jpg,.jpeg,.png,.pdf"
                disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleDocumentUpload(file, isGuardianDocument);
                  }
                }}
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  const handleSave = async () => {
    if (!client) return;
    const showGuardianError = (message: string) => {
      setError(message);
      setActiveTab('guardian');
      showToast(message, 'error', 7000);
    };

    if (!client.firstName.trim()) {
      setError('Le prénom est requis.');
      setActiveTab('personal');
      return;
    }
    if (phoneState.displayValue.trim() && (!phoneState.isValid || !phoneState.e164Value)) {
      const message = client.isMinor
        ? 'Le numéro de téléphone du client mineur est invalide pour le pays sélectionné.'
        : 'Le numéro de téléphone du client est invalide pour le pays sélectionné.';
      setError(message);
      showToast(message, 'error', 7000);
      return;
    }
    if (emergencyPhoneState.displayValue.trim() && !emergencyPhoneState.isValid) {
      setError('Le numéro du contact d’urgence est invalide pour le pays sélectionné.');
      return;
    }
    if (client.legalGuardian?.phone.trim() && !guardianPhoneState.isValid) {
      showGuardianError('Le numéro du représentant légal est invalide pour le pays sélectionné.');
      return;
    }
    if (
      client.legalGuardian?.email.trim()
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.legalGuardian.email.trim())
    ) {
        showGuardianError('L’adresse email du représentant légal est invalide.');
        return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      
      // Clean the client data before sending - handle dates properly for PostgreSQL
      const clientToSave = applyMedicalListTextToClient(client);
      const cleanedClient = {
        ...clientToSave,
        phone: phoneState.e164Value ?? '',
        emergencyContact: {
          ...clientToSave.emergencyContact,
          phone: emergencyPhoneState.e164Value ?? '',
        },
        legalGuardian: clientToSave.legalGuardian
          ? { ...clientToSave.legalGuardian, phone: guardianPhoneState.e164Value ?? '' }
          : undefined,
        dateOfBirth: (clientToSave.dateOfBirth && typeof clientToSave.dateOfBirth === 'string' && clientToSave.dateOfBirth.trim()) 
          ? clientToSave.dateOfBirth 
          : null,
        createdAt: undefined,
        updatedAt: undefined,
      };
      
      const response = await apiClient(`/api/admin/clients/${client.id}`, {
        method: 'PUT',
        body: JSON.stringify(cleanedClient),
      });

      const data = await response.json();
      if (data.success) {
        setHasUnsavedChanges(false);
        setClient((prev) => {
          if (!prev) return prev;
          const updated = applyMedicalListTextToClient(prev);
          syncMedicalListTextFromClient(updated);
          return updated;
        });
        
        showToast('Client mis à jour avec succès', 'success');
      } else {
        const message = data.message || 'Échec de la sauvegarde du client';
        setError(message);
        showToast(message, 'error', 7000);
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Save client error:', error);
      const message = 'Erreur de connexion au serveur';
      setError(message);
      showToast(message, 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?')) {
        navigate('/clients');
      }
    } else {
      navigate('/clients');
    }
  };

  const tabs = [
    {
      id: 'personal',
      label: 'Informations Personnelles',
      icon: User,
      description: 'Nom, contact et adresse'
    },
    {
      id: 'identity',
      label: 'Pièce d\'Identité',
      icon: Shield,
      description: 'Documents d\'identité'
    },
    {
      id: 'guardian',
      label: 'Représentant Légal',
      icon: UserCheck,
      description: 'Informations du tuteur (si mineur)',
      hidden: !client?.isMinor
    },
    {
      id: 'medical',
      label: 'Informations Médicales',
      icon: Heart,
      description: 'Allergies, médicaments, conditions'
    },
    {
      id: 'preferences',
      label: 'Préférences & Confidentialité',
      icon: Settings,
      description: 'Communications et vie privée'
    }
  ].filter(tab => !tab.hidden);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du client...</span>
      </div>
    );
  }

  // Only loading failures replace the page. Save/validation errors must keep
  // the edited form mounted so none of the user's changes are lost.
  if (!client) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Client non trouvé'}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate('/clients')}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des clients
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ToastComponent />

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Modifier: {getClientEditTitleName(client)}</span>
              <HelpIcon 
                docPath="05-gestion-clients#05-6-modification" 
                tooltip="Documentation : Modification d'un client"
              />
            </h1>
            {hasUnsavedChanges && (
              <p className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1">
                Modifications non sauvegardées
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className={`mr-2 h-5 w-5 ${
                  activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                <div className="text-left">
                  <div>{tab.label}</div>
                  <div className="text-xs text-gray-400 hidden sm:block">{tab.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <div className="space-y-8">
        {/* Personal Information Tab */}
        {activeTab === 'personal' && (
          <>
            {/* Basic Information */}
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                  <User className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Informations de Base</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    value={client.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Nom (optionnel)
                  </label>
                  <input
                    type="text"
                    value={client.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Adresse Email (optionnel)
                  </label>
                  <input
                    type="email"
                    value={client.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Numéro de Téléphone (optionnel)
                  </label>
                  <InternationalPhoneInput
                    value={phoneState.displayValue}
                    country={phoneState.country}
                    onChange={(result) => {
                      setPhoneState(result);
                      handleInputChange('phone', result.displayValue);
                    }}
                    selectClassName="input-field"
                    inputClassName="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date de Naissance (optionnel)
                  </label>
                  <DateInput
                    value={client.dateOfBirth || ''}
                    disabled={client.isMinor === true && !client.dateOfBirth}
                    onChange={(value) => handleInputChange('dateOfBirth', value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Mineur (optionnel)
                  </label>
                  <label className="flex items-center gap-2 pt-2 text-sm text-textPrimary">
                    <input
                      type="checkbox"
                      checked={client.isMinor === true}
                      disabled={Boolean(client.dateOfBirth)}
                      onChange={(e) => handleInputChange('isMinor', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    Le client est mineur
                  </label>
                  {client.dateOfBirth && (
                    <p className="mt-1 text-xs text-gray-500">
                      Calculé automatiquement depuis la date de naissance.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Statut
                  </label>
                  <select
                    value={client.status || 'ACTIVE'}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="input-field"
                  >
                    <option value="ACTIVE">Actif</option>
                    <option value="INACTIVE">Inactif</option>
                    <option value="SUSPENDED">Suspendu</option>
                    <option value="VIP">VIP</option>
                  </select>
                </div>
              </div>

              {client.isMinor && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="text-yellow-800 font-medium">
                      Client mineur
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Address Information */}
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-accent-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Adresse</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={client.address?.street || ''}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={client.address?.city || ''}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Code Postal
                  </label>
                  <input
                    type="text"
                    value={client.address?.postalCode || ''}
                    onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Pays
                  </label>
                  <input
                    type="text"
                    value={client.address?.country || ''}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Contact d'Urgence</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Nom du Contact
                  </label>
                  <input
                    type="text"
                    value={client.emergencyContact?.name || ''}
                    onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Relation
                  </label>
                  <input
                    type="text"
                    value={client.emergencyContact?.relationship || ''}
                    onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Numéro de Téléphone
                  </label>
                  <InternationalPhoneInput
                    value={emergencyPhoneState.displayValue}
                    country={emergencyPhoneState.country}
                    onChange={(result) => {
                      setEmergencyPhoneState(result);
                      handleInputChange('emergencyContact.phone', result.displayValue);
                    }}
                    selectClassName="input-field"
                    inputClassName="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Adresse Email
                  </label>
                  <input
                    type="email"
                    value={client.emergencyContact?.email || ''}
                    onChange={(e) => handleInputChange('emergencyContact.email', e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Identity Document Tab */}
        {activeTab === 'identity' && (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Pièce d'Identité</h2>
            </div>

            <SensitiveDataBanner
              message="Les pièces d'identité sont des données sensibles (Tier B) : chiffrées en base et sur les fichiers associés. Accès réservé au personnel habilité ; ne pas partager en dehors du dossier client."
              className="mb-6"
            />

            {client.identityDocument ? (
              <div className="space-y-6">
                {/* Document File Management */}
                {renderDocumentSection(client.identityDocument, false)}
                
                {/* Document Information Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Type de Document
                    </label>
                    <select
                      value={client.identityDocument?.type || 'national_id'}
                      onChange={(e) => handleInputChange('identityDocument.type', e.target.value)}
                      className="input-field"
                    >
                      <option value="national_id">Carte d'identité nationale</option>
                      <option value="passport">Passeport</option>
                      <option value="driving_license">Permis de conduire</option>
                      <option value="birth_certificate">Acte de naissance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Numéro de Document
                    </label>
                    <input
                      type="text"
                      value={client.identityDocument?.number || ''}
                      onChange={(e) => handleInputChange('identityDocument.number', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Date d'Émission
                    </label>
                    <DateInput
                      value={formatDateForInput(client.identityDocument?.issueDate)}
                      onChange={(value) => handleInputChange('identityDocument.issueDate', value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Date d'Expiration
                    </label>
                    <DateInput
                      value={formatDateForInput(client.identityDocument?.expiryDate)}
                      onChange={(value) => handleInputChange('identityDocument.expiryDate', value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Lieu d'Émission
                    </label>
                    <input
                      type="text"
                      value={client.identityDocument?.issuePlace || ''}
                      onChange={(e) => handleInputChange('identityDocument.issuePlace', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Autorité d'Émission
                    </label>
                    <input
                      type="text"
                      value={client.identityDocument?.issuingAuthority || ''}
                      onChange={(e) => handleInputChange('identityDocument.issuingAuthority', e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
                
                {/* Remove Document Button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleInputChange('identityDocument', null)}
                    className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-300"
                  >
                    <X className="w-4 h-4" />
                    <span>Supprimer la pièce d'identité</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Aucune pièce d'identité enregistrée</p>
                <button
                  type="button"
                  onClick={() => handleInputChange('identityDocument', {
                    type: 'national_id',
                    number: '',
                    issueDate: '',
                    expiryDate: '',
                    issuePlace: '',
                    issuingAuthority: '',
                    fileUrl: '',
                    fileName: ''
                  })}
                  className="mt-4 btn-secondary"
                >
                  Ajouter une pièce d'identité
                </button>
              </div>
            )}
          </div>
        )}

        {/* Legal Guardian Tab */}
        {activeTab === 'guardian' && client.isMinor && (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Représentant Légal</h2>
            </div>

            {client.legalGuardian ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Prénom du Représentant
                    </label>
                    <input
                      type="text"
                      value={client.legalGuardian?.firstName || ''}
                      onChange={(e) => handleInputChange('legalGuardian.firstName', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Nom du Représentant
                    </label>
                    <input
                      type="text"
                      value={client.legalGuardian?.lastName || ''}
                      onChange={(e) => handleInputChange('legalGuardian.lastName', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Email du Représentant
                    </label>
                    <input
                      type="email"
                      value={client.legalGuardian?.email || ''}
                      onChange={(e) => handleInputChange('legalGuardian.email', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Téléphone du Représentant
                    </label>
                    <InternationalPhoneInput
                      value={guardianPhoneState.displayValue}
                      country={guardianPhoneState.country}
                      onChange={(result) => {
                        setGuardianPhoneState(result);
                        handleInputChange('legalGuardian.phone', result.displayValue);
                      }}
                      selectClassName="input-field"
                      inputClassName="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Relation avec le Mineur
                    </label>
                    <select
                      value={client.legalGuardian?.relationshipToMinor || 'parent'}
                      onChange={(e) => handleInputChange('legalGuardian.relationshipToMinor', e.target.value)}
                      className="input-field"
                    >
                      <option value="parent">Parent</option>
                      <option value="legal_guardian">Tuteur légal</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>

                  {client.legalGuardian.relationshipToMinor === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Précisions sur la Relation
                      </label>
                      <input
                        type="text"
                        value={client.legalGuardian?.relationshipDetails || ''}
                        onChange={(e) => handleInputChange('legalGuardian.relationshipDetails', e.target.value)}
                        className="input-field"
                      />
                    </div>
                  )}
                </div>

                {/* Guardian Identity Document */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-textPrimary mb-4">Pièce d'Identité du Représentant</h3>
                  
                  {/* Guardian Document File Management */}
                  {renderDocumentSection(client.legalGuardian.identityDocument, true)}
                  
                  {/* Guardian Document Information Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Type de Document
                      </label>
                      <select
                        value={client.legalGuardian?.identityDocument?.type || 'national_id'}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.type', e.target.value)}
                        className="input-field"
                      >
                        <option value="national_id">Carte d'identité nationale</option>
                        <option value="passport">Passeport</option>
                        <option value="driving_license">Permis de conduire</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Numéro de Document
                      </label>
                      <input
                        type="text"
                        value={client.legalGuardian?.identityDocument?.number || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.number', e.target.value)}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Date d'Émission
                      </label>
                      <DateInput
                        value={formatDateForInput(client.legalGuardian?.identityDocument?.issueDate)}
                        onChange={(value) => handleInputChange('legalGuardian.identityDocument.issueDate', value)}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Date d'Expiration
                      </label>
                      <DateInput
                        value={formatDateForInput(client.legalGuardian?.identityDocument?.expiryDate)}
                        onChange={(value) => handleInputChange('legalGuardian.identityDocument.expiryDate', value)}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Lieu d'Émission
                      </label>
                      <input
                        type="text"
                        value={client.legalGuardian?.identityDocument?.issuePlace || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuePlace', e.target.value)}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Autorité d'Émission
                      </label>
                      <input
                        type="text"
                        value={client.legalGuardian?.identityDocument?.issuingAuthority || ''}
                        onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuingAuthority', e.target.value)}
                        className="input-field"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <UserCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Aucun représentant légal enregistré</p>
                <p className="text-sm">Ce client est mineur mais n'a pas de représentant légal assigné</p>
                <button
                  type="button"
                  onClick={handleAddLegalGuardian}
                  className="btn-primary mt-5 inline-flex items-center gap-2"
                >
                  <UserCheck className="h-4 w-4" />
                  Ajouter un représentant légal
                </button>
              </div>
            )}
          </div>
        )}

        {/* Medical Information Tab */}
        {activeTab === 'medical' && (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                <Heart className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Informations Médicales</h2>
            </div>

            <SensitiveDataBanner
              message="Les informations médicales sont des données de santé (Tier B) : chiffrées en base, consultables uniquement pour la sécurité des actes. Ne pas les utiliser à d'autres fins."
              className="mb-6"
            />

            {client.medicalInfo ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Allergies
                    </label>
                    <textarea
                      value={medicalListText.allergies}
                      onChange={(e) => {
                        setMedicalListText((prev) => ({ ...prev, allergies: e.target.value }));
                        setHasUnsavedChanges(true);
                      }}
                      onBlur={(e) => handleInputChange('medicalInfo.allergies', parseCommaSeparatedList(e.target.value))}
                      rows={2}
                      className="input-field"
                      placeholder="Séparer par des virgules"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Médicaments
                    </label>
                    <textarea
                      value={medicalListText.medications}
                      onChange={(e) => {
                        setMedicalListText((prev) => ({ ...prev, medications: e.target.value }));
                        setHasUnsavedChanges(true);
                      }}
                      onBlur={(e) => handleInputChange('medicalInfo.medications', parseCommaSeparatedList(e.target.value))}
                      rows={2}
                      className="input-field"
                      placeholder="Séparer par des virgules"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Conditions Médicales
                    </label>
                    <textarea
                      value={medicalListText.conditions}
                      onChange={(e) => {
                        setMedicalListText((prev) => ({ ...prev, conditions: e.target.value }));
                        setHasUnsavedChanges(true);
                      }}
                      onBlur={(e) => handleInputChange('medicalInfo.conditions', parseCommaSeparatedList(e.target.value))}
                      rows={2}
                      className="input-field"
                      placeholder="Séparer par des virgules"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Groupe Sanguin
                    </label>
                    <select
                      value={client.medicalInfo?.bloodType || ''}
                      onChange={(e) => handleInputChange('medicalInfo.bloodType', e.target.value)}
                      className="input-field"
                    >
                      <option value="">Non spécifié</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Statut de Grossesse
                    </label>
                    <select
                      value={client.medicalInfo?.pregnancyStatus || ''}
                      onChange={(e) => handleInputChange('medicalInfo.pregnancyStatus', e.target.value)}
                      className="input-field"
                    >
                      <option value="">Non applicable</option>
                      <option value="not_pregnant">Non enceinte</option>
                      <option value="pregnant">Enceinte</option>
                      <option value="breastfeeding">Allaitement</option>
                      <option value="trying_to_conceive">Essaie de concevoir</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Dernier Vaccin Tétanos
                    </label>
                    <DateInput
                      value={formatDateForInput(client.medicalInfo?.lastTetanusShot)}
                      onChange={(value) => handleInputChange('medicalInfo.lastTetanusShot', value)}
                      className="input-field"
                    />
                  </div>
                </div>
                
                {/* Remove Medical Info Button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('medicalInfo', null);
                      setMedicalListText({ allergies: '', medications: '', conditions: '' });
                    }}
                    className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-300"
                  >
                    <X className="w-4 h-4" />
                    <span>Supprimer les informations médicales</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Heart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Aucune information médicale enregistrée</p>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('medicalInfo', {
                      allergies: [],
                      medications: [],
                      conditions: [],
                      bloodType: '',
                      pregnancyStatus: '',
                      lastTetanusShot: '',
                    });
                    setMedicalListText({ allergies: '', medications: '', conditions: '' });
                  }}
                  className="mt-4 btn-secondary"
                >
                  Ajouter des informations médicales
                </button>
              </div>
            )}
          </div>
        )}

        {/* Preferences & Privacy Tab */}
        {activeTab === 'preferences' && (
          <>
            {/* Communication Preferences */}
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Préférences de Communication</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Méthode de Communication Préférée
                  </label>
                  <select
                    value={client.preferences?.communicationMethod || 'email'}
                    onChange={(e) => handleInputChange('preferences.communicationMethod', e.target.value)}
                    className="input-field"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="phone">Téléphone</option>
                    <option value="app">Application</option>
                  </select>
                  <p className="mt-1 text-xs text-textSecondary">
                    Indication générale uniquement — n&apos;active pas l&apos;envoi de SMS.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Rappels Activés
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={client.preferences?.reminderSettings?.enabled || false}
                      onChange={(e) => handleInputChange('preferences.reminderSettings.enabled', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-textPrimary">Activer les rappels automatiques</span>
                  </label>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-textPrimary mb-1">Notifications SMS (effectives)</h3>
                <p className="text-xs text-textSecondary mb-4">
                  Ces options contrôlent réellement l&apos;envoi de SMS (confirmation, modification, rappel).
                  Les trois conditions doivent être remplies : consentement, téléphone vérifié, canal SMS.
                </p>
                <div className="space-y-4">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={!!client.smsConsent}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleInputChange('smsConsent', checked);
                        const channels = new Set(client.reminderChannels || ['email']);
                        if (checked) channels.add('sms');
                        else channels.delete('sms');
                        if (!channels.has('email')) channels.add('email');
                        handleInputChange('reminderChannels', Array.from(channels));
                      }}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm text-textPrimary font-medium">Consentement SMS (RGPD)</span>
                      <p className="text-xs text-gray-500">Le client accepte de recevoir des SMS transactionnels</p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={!!client.phoneVerified}
                      onChange={(e) => handleInputChange('phoneVerified', e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={!client.phone}
                    />
                    <div>
                      <span className="text-sm text-textPrimary font-medium">Téléphone vérifié</span>
                      <p className="text-xs text-gray-500">
                        {client.phone
                          ? `Marquer ${client.phone} comme vérifié (sinon OTP côté espace client)`
                          : 'Ajoutez d’abord un numéro de téléphone'}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={(client.reminderChannels || []).includes('sms')}
                      onChange={(e) => {
                        const channels = new Set(client.reminderChannels || ['email']);
                        if (e.target.checked) channels.add('sms');
                        else channels.delete('sms');
                        if (!channels.has('email')) channels.add('email');
                        handleInputChange('reminderChannels', Array.from(channels));
                      }}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={!client.smsConsent}
                    />
                    <div>
                      <span className="text-sm text-textPrimary font-medium">Canal SMS activé</span>
                      <p className="text-xs text-gray-500">Inclure SMS dans les canaux de notification du client</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Privacy Settings */}
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-secondary-100 rounded-lg">
                  <Shield className="w-5 h-5 text-secondary-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Paramètres de Confidentialité</h2>
              </div>

              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={client.preferences?.privacySettings?.allowPhotography || false}
                    onChange={(e) => handleInputChange('preferences.privacySettings.allowPhotography', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm text-textPrimary font-medium">Autoriser la photographie pendant les séances</span>
                    <p className="text-xs text-gray-500">Permet de prendre des photos pendant les interventions</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={client.preferences?.privacySettings?.allowGalleryUse || false}
                    onChange={(e) => handleInputChange('preferences.privacySettings.allowGalleryUse', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm text-textPrimary font-medium">Autoriser l'utilisation des photos dans la galerie</span>
                    <p className="text-xs text-gray-500">Les photos peuvent être utilisées dans le portfolio public</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={client.preferences?.privacySettings?.allowTestimonials || false}
                    onChange={(e) => handleInputChange('preferences.privacySettings.allowTestimonials', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm text-textPrimary font-medium">Autoriser l'utilisation de témoignages</span>
                    <p className="text-xs text-gray-500">Permet d'utiliser les avis et témoignages du client</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={client.preferences?.privacySettings?.allowMarketing || false}
                    onChange={(e) => handleInputChange('preferences.privacySettings.allowMarketing', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm text-textPrimary font-medium">Autoriser les communications marketing</span>
                    <p className="text-xs text-gray-500">Recevoir des offres promotionnelles et actualités</p>
                  </div>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
      </form>

      {/* Save Button (Mobile) */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>
    </div>
  );
}
